import { Dispatch, useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Flex,
  SearchInput,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { IconPlus, IconTrashCan } from '@optimizely/axiom-icons';

import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useGetTools } from '@frontend/features/tools-management/hooks/useTools';
import * as agentStyles from '@frontend/styles/form.css';
import { Tool } from '@frontend/types/tools';

import { styles } from './ToolsInput.css';

type ToolsInputProps = {
  agentToolNames: string[];
  dispatch: Dispatch<SpecializedAgentAction>;
  opalInstanceId: string;
  disabled?: boolean;
};

export function ToolsInput({
  agentToolNames,
  dispatch,
  opalInstanceId,
  disabled = false,
}: ToolsInputProps) {
  const toolText =
    'Specify tools for your agent. Once added, refer to them in your prompt template with `tool_name`';

  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);
  const [toolSearchValue, setToolSearchValue] = useState('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [toolToRemove, setToolToRemove] = useState<string | null>(null);

  const { toolsResponse, isPending: toolsLoading } = useGetTools(opalInstanceId, true);

  const availableTools = useMemo(() => {
    return toolsResponse?.functions || [];
  }, [toolsResponse?.functions]);

  const toolsByName = useMemo(() => {
    return new Map(availableTools.map((tool: Tool) => [tool.name, tool]));
  }, [availableTools]);

  // Filter tools based on search value and sort alphabetically by name
  const filteredTools = useMemo(() => {
    return (
      availableTools
        ?.filter(
          (tool: Tool) =>
            tool.name.toLowerCase().includes(toolSearchValue.toLowerCase()) ||
            tool.description.toLowerCase().includes(toolSearchValue.toLowerCase())
        )
        .sort((a: Tool, b: Tool) => a.name.localeCompare(b.name)) || []
    );
  }, [availableTools, toolSearchValue]);

  const handleOpenToolDialog = () => {
    setSelectedToolIds([]);
    setToolSearchValue('');
    setToolDialogOpen(true);
  };

  const handleCloseToolDialog = () => {
    setToolDialogOpen(false);
    setSelectedToolIds([]);
    setToolSearchValue('');
  };

  const handleSelectTool = (toolId: string) => {
    setSelectedToolIds(ids =>
      ids.includes(toolId) ? ids.filter(id => id !== toolId) : [...ids, toolId]
    );
  };

  const handleAddSelectedTools = () => {
    // Find the selected tools by function_id
    const newToolNames = availableTools
      .filter((tool: Tool) => selectedToolIds.includes(tool.function_id))
      .map((tool: Tool) => tool.name)
      .filter((name: string) => !agentToolNames.includes(name)); // Prevent duplicates

    const updatedTools = [...agentToolNames, ...newToolNames];

    dispatch({ type: 'UPDATE_NESTED', field: 'enabled_tools', value: updatedTools });
    setToolDialogOpen(false);
    setSelectedToolIds([]);
  };

  const handleOpenRemoveDialog = (toolName: string) => {
    setToolToRemove(toolName);
    setRemoveDialogOpen(true);
  };

  const handleRemoveTool = () => {
    if (toolToRemove) {
      const updatedTools = agentToolNames.filter(name => name !== toolToRemove);
      dispatch({ type: 'UPDATE_NESTED', field: 'enabled_tools', value: updatedTools });
    }
    setRemoveDialogOpen(false);
    setToolToRemove(null);
  };

  return (
    <Flex flexDirection="column" gap="8" pb="8" className={agentStyles.FormInputFieldMaxWidth}>
      <Flex flexDirection="row" gap="16" justifyContent="space-between">
        <Field
          label="Tools"
          info="Extends what Opal can do, such as executing actions or pulling information from other services"
        />
        {agentToolNames.length > 0 && (
          <Button
            appearance="default"
            addonBefore={<IconPlus size="16" />}
            onClick={handleOpenToolDialog}
            disabled={disabled}
          >
            Add Tool
          </Button>
        )}
      </Flex>
      {agentToolNames.length > 0 ? (
        <Flex flexDirection="column" gap="8">
          {agentToolNames.map(toolName => {
            const tool = toolsByName.get(toolName);
            return (
              <Card key={toolName} p="16">
                <CardHeader
                  description={
                    tool?.description ? (
                      <Tooltip auto content={tool.description}>
                        <Text lineClamp="2">{tool.description}</Text>
                      </Tooltip>
                    ) : undefined
                  }
                  addonAfter={
                    <Tooltip content="Remove">
                      <Button
                        appearance="subtle"
                        size="sm"
                        icon={<IconTrashCan size={16} />}
                        onClick={() => handleOpenRemoveDialog(toolName)}
                        aria-label="Remove tool"
                        disabled={disabled}
                      />
                    </Tooltip>
                  }
                >
                  <Tooltip auto content={tool?.description}>
                    <Text>{toolName}</Text>
                  </Tooltip>
                </CardHeader>
              </Card>
            );
          })}
        </Flex>
      ) : (
        <Flex
          className={styles.emptySection}
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          bg="bg.secondary"
          p="20"
        >
          <Text color="fg.secondary">{toolText}</Text>
          <Button
            appearance="subtle"
            addonBefore={<IconPlus size="16" />}
            onClick={handleOpenToolDialog}
            disabled={disabled}
          >
            Add Tool
          </Button>
        </Flex>
      )}

      {/* Tool Selection Dialog */}
      <Dialog open={toolDialogOpen} onOpenChange={setToolDialogOpen}>
        <DialogContent size="lg">
          <DialogHeader>Select Tools</DialogHeader>
          <DialogBody style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {toolsLoading ? (
              <Text>Loading tools...</Text>
            ) : (
              <Flex flexDirection="column" gap="16">
                <SearchInput
                  placeholder="Search tools..."
                  value={toolSearchValue}
                  onChange={e => setToolSearchValue(e.target.value)}
                  w="full"
                  h="40"
                />
                <div className={styles.toolsGrid}>
                  {filteredTools && filteredTools.length > 0 ? (
                    filteredTools.map((tool: Tool) => {
                      const isSelected = selectedToolIds.includes(tool.function_id);
                      const isDisabled = agentToolNames.some(t => t === tool.name);
                      const cardClassName = isSelected
                        ? styles.toolCardSelected
                        : isDisabled
                          ? styles.toolCardDisabled
                          : styles.toolCard;

                      return (
                        <Card
                          key={tool.function_id}
                          p="16"
                          className={cardClassName}
                          onClick={() => !isDisabled && handleSelectTool(tool.function_id)}
                        >
                          <CardHeader
                            description={
                              <Tooltip content={tool.description}>
                                <Text lineClamp="2">{tool.description}</Text>
                              </Tooltip>
                            }
                            addonBefore={
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleSelectTool(tool.function_id)}
                                disabled={isDisabled}
                              />
                            }
                          >
                            {tool.name}
                          </CardHeader>
                        </Card>
                      );
                    })
                  ) : (
                    <div className={styles.noToolsMessage}>
                      <Text>
                        {toolSearchValue
                          ? `No tools found matching "${toolSearchValue}".`
                          : 'No tools available.'}
                      </Text>
                    </div>
                  )}
                </div>
              </Flex>
            )}
          </DialogBody>
          <DialogFooter>
            <Button appearance="default" onClick={handleCloseToolDialog}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleAddSelectedTools}
              disabled={selectedToolIds.length === 0}
            >
              Add Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog for remove confirmation */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>Remove Tool?</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to remove the tool &quot;{toolToRemove}&quot;?
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRemoveDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction appearance="danger" onClick={handleRemoveTool}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Flex>
  );
}
